import React, { useState } from "react";
import { cn } from "@/utils/utils";
import Text from "@/components/ui/text";
import { ServiceItem, PricingModel } from "../types";
import { useServiceItemTypes } from "../hooks";
import { formatCompletePricingText } from "../utils";
import { DraggableProvided, DraggableStateSnapshot } from "@hello-pangea/dnd";
import IconContainer from "@/components/ui/icon-container";
import { IconButton } from "@/components/ui/icon-button";
import { useTranslation } from "react-i18next";
import {
  ChevronUp,
  GripVertical,
  ChevronDown,
  Edit,
  Delete
} from "@dreamhub/designsystem/icons";
const TRANSLATION_KEY = "settings.product";

interface ServiceItemRowProps {
  serviceItem: ServiceItem;
  provided: DraggableProvided;
  snapshot: DraggableStateSnapshot;
  onEditServiceItem?: (serviceItem: ServiceItem) => void;
  onDeleteServiceItem?: (serviceItem: ServiceItem) => void;
}

export const ServiceItemRow: React.FC<ServiceItemRowProps> = ({
  serviceItem,
  provided,
  snapshot,
  onEditServiceItem,
  onDeleteServiceItem
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const { data: serviceItemTypes = [] } = useServiceItemTypes();

  const serviceType = serviceItemTypes.find(
    (type) => type.id === serviceItem.typeId
  );

  const basePrice = serviceItem.pricing[0];
  const hasMultiplePricing = serviceItem.pricing.length > 1;

  const formatPrice = (
    price: number | null,
    frequency: number | null,
    pricingModel: number,
    serviceDuration?: number | null,
    chargeType?: number | null
  ) => {
    return formatCompletePricingText(
      price,
      pricingModel,
      frequency,
      serviceDuration || null,
      t,
      chargeType
    );
  };

  const dragProps = {
    ref: provided.innerRef,
    ...provided.draggableProps,
    style: provided.draggableProps.style
  };

  return (
    <>
      <div
        {...dragProps}
        data-test={`products-service-row-${serviceItem.id}`}
        className={cn(
          "flex items-center",
          "min-h-[44px] px-4 py-2",
          "bg-transparent",
          "hover:bg-surface-hover-soft transition-colors duration-200",
          snapshot.isDragging && [
            "bg-surface-focus-selected",
            "shadow-[0px_12px_24px_-4px_rgba(0,0,0,0.20),0px_0px_2px_-2px_rgba(0,0,0,0.18)]",
            "relative z-50",
            "rounded-md"
          ],
          "group"
        )}
      >
        <div className="w-6 flex items-center">
          <div
            {...provided.dragHandleProps}
            data-test={`products-service-row-drag-${serviceItem.id}`}
            className={cn(
              "cursor-grab active:cursor-grabbing",
              "opacity-0 group-hover:opacity-100 transition-opacity"
            )}
          >
            <IconContainer size="sm" color="tertiary">
              <GripVertical />
            </IconContainer>
          </div>
        </div>

        <div className="w-[180px] flex items-center">
          <Text
            variant="body"
            className="break-words leading-tight"
            data-test={`products-service-row-name-${serviceItem.id}`}
          >
            {serviceItem.name}
          </Text>
        </div>

        <div className="w-[180px] flex items-center">
          <Text
            variant="body"
            className="truncate capitalize"
            data-test={`products-service-row-type-${serviceItem.id}`}
          >
            {serviceType?.name || serviceItem.typeId}
          </Text>
        </div>

        <div className="flex-1 flex items-center gap-2">
          <div
            className="flex-1"
            data-test={`products-service-row-pricing-${serviceItem.id}`}
          >
            {serviceItem.pricingModel === PricingModel.FLEXIBLE ? (
              <Text variant="body">
                {t(`${TRANSLATION_KEY}.serviceItemRow.flexiblePricing`)}
              </Text>
            ) : hasMultiplePricing ? (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="flex items-center gap-1 hover:bg-surface-hover-soft p-1 rounded transition-colors"
                data-test={`products-service-row-view-pricing-${serviceItem.id}`}
              >
                <Text variant="body">
                  {isExpanded
                    ? t(`${TRANSLATION_KEY}.serviceItemRow.hidePricing`)
                    : t(`${TRANSLATION_KEY}.serviceItemRow.viewPricing`)}
                </Text>
                {isExpanded ? (
                  <IconContainer size="sm">
                    <ChevronUp />
                  </IconContainer>
                ) : (
                  <IconContainer size="sm">
                    <ChevronDown />
                  </IconContainer>
                )}
              </button>
            ) : basePrice ? (
              <div className="flex items-center gap-1">
                <Text variant="body">
                  {formatPrice(
                    basePrice.price,
                    basePrice.billingFrequency,
                    serviceItem.pricingModel,
                    basePrice.serviceItemPeriods,
                    serviceItem.chargeType
                  )}
                </Text>
              </div>
            ) : (
              <Text variant="bodySecondary">
                {t(`${TRANSLATION_KEY}.serviceItemRow.noPricingSet`)}
              </Text>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <IconButton
            variant="iconNoBg"
            size="sm"
            onClick={() => onEditServiceItem?.(serviceItem)}
            iconColor="primary"
            data-test={`products-service-row-edit-${serviceItem.id}`}
          >
            <Edit />
          </IconButton>
          <IconButton
            variant="iconNoBg"
            size="sm"
            destructive
            onClick={() => onDeleteServiceItem?.(serviceItem)}
            data-test={`products-service-row-delete-${serviceItem.id}`}
          >
            <Delete />
          </IconButton>
        </div>
      </div>

      <div className="border-b border-border-border1"></div>

      {isExpanded && hasMultiplePricing && (
        <div
          data-test={`products-service-row-pricing-expanded-${serviceItem.id}`}
        >
          {serviceItem.pricing.map((pricing) => (
            <div
              key={pricing.id}
              data-test={`products-service-row-pricing-item-${pricing.id}`}
              className="flex items-center min-h-[44px] px-4 py-2 bg-transparent hover:bg-surface-hover-soft transition-colors duration-200"
            >
              <div className="w-6"></div>
              <div className="w-[180px]"></div>
              <div className="w-[180px]"></div>
              <div className="flex-1 flex items-center">
                <Text variant="body">
                  {formatPrice(
                    pricing.price,
                    pricing.billingFrequency,
                    serviceItem.pricingModel,
                    pricing.serviceItemPeriods,
                    serviceItem.chargeType
                  )}
                </Text>
              </div>
              <div className="flex items-center gap-1 w-[72px]"></div>
            </div>
          ))}
          <div className="border-b border-border-border1"></div>
        </div>
      )}
    </>
  );
};
